/**
 * A placeholder token that survived substitution. Same grammar as
 * `substitutePlaceholders`' `PLACEHOLDER_RE`, which passes an unresolved token
 * through as its literal `{…}` text so an editor sees the typo.
 */
export const UNRESOLVED_TOKEN_RE = /\{[\w.[\]-]+\}/;

/**
 * Substitutes an editor-typed link target, so one Template Page can link per
 * URL — `/nachhilfe/{location.slug}/jobs/` on the Location Template,
 * `/standorte/{city.slug}-nachhilfe/jobs/` on the City Template (SH-497).
 *
 * Returns `''` when a token is still unresolved afterwards. A literal token is
 * the right signal in body text and the wrong one in an `href`: it links to
 * `/nachhilfe/%7Blocation.slug%7D/jobs/`, a guaranteed 404 that crawlers
 * follow. Every caller already treats an empty target as "no link", so the
 * link drops and its label — which keeps its own literal token, if any —
 * remains the editor's signal. See 70.11 § Where Tokens Resolve.
 */
export function substituteHref(
  href: string | null | undefined,
  sub: (value: string) => string,
): string {
  if (!href) {
    return '';
  }
  const resolved = sub(href).trim();
  return UNRESOLVED_TOKEN_RE.test(resolved) ? '' : resolved;
}

/**
 * Substitutes a link target written as a `|`-separated fallback chain, and
 * returns the first candidate that resolves — the `??` of editor-typed links
 * (SH-524). `/{city.slug}|/nachhilfe/{location.slug}` links to the city on a
 * City Page and to the Standort everywhere else, from one CTA on one Template
 * Page.
 *
 * A candidate "resolves" when `substituteHref` returns something for it: no
 * `{…}` token survived, and what is left is not blank. The blank test matters
 * because a blanking-entity token (§ `BLANKING_ENTITY_PREFIXES`) resolves to
 * the empty string instead of surviving as literal text — without it, a
 * `{category.slug}` candidate would win the chain and link nowhere. It only
 * catches a candidate that is *entirely* such a token; `/{category.slug}`
 * still leaves `/` behind, and that counts as resolved.
 *
 * Candidates are tried left to right and the rest are never evaluated, so the
 * leftmost is the most specific one. A target without a `|` behaves exactly as
 * `substituteHref` — including returning `''` for "no link", which every
 * caller already treats as "render nothing".
 *
 * A literal pipe in a URL must be written `%7C`, as RFC 3986 requires anyway.
 */
export function substituteHrefChain(
  href: string | null | undefined,
  sub: (value: string) => string,
): string {
  if (!href) {
    return '';
  }
  for (const candidate of href.split('|')) {
    const resolved = substituteHref(candidate, sub);
    if (resolved) {
      return resolved;
    }
  }
  return '';
}
